import React, { FC } from 'react';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Typography,
    CircularProgress,
    Button,
    Box,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import { Order } from 'models/order';
import DeliveryScheduleTable, { DeliveryScheduleSelectionProps } from './delivery-schedule-table.component';
import DeliverySchedulePrintMenu from './delivery-schedule-print-menu.component';
import { useDeliverySchedulePrint } from './use-delivery-schedule-print.hook';
import AlertSnackbar from 'components/shared/alert-snackbar/alert-snackbar.component';

type AccordionStyle = 'late' | 'normal' | 'completed';

type DeliveryScheduleAccordionProps = DeliveryScheduleSelectionProps & {
    date: Date;
    clinicId?: string;
    departmentId?: string;
    orders: Order[];
    style: AccordionStyle;
    title: string;
    onSentByClinicDialog?: (order: Order) => void;
    onRemoveFromScheduleDialog?: (order: Order) => void;
    onChangeInternalDeliveryDate?: (order: Order) => void;
    onChangeExternalDeliveryDate?: (order: Order) => void;
    defaultExpanded?: boolean;
    isExternal: boolean;
    // DD-3546: the parent decides whether the Print menu is offered at all (not on the external tab).
    isPrintAvailable?: boolean;
    // "Print list" is the planned list, so it is only enabled while the table shows it.
    isListPrintAvailable?: boolean;
    onShowMore?: () => void;
    showMoreVisible?: boolean;
    loadingMore?: boolean;
};
type IconProps = {
    style: AccordionStyle;
};

const DeliveryScheduleAccordion: FC<DeliveryScheduleAccordionProps> = ({
    date,
    clinicId,
    departmentId,
    orders,
    style,
    title,
    onSentByClinicDialog,
    onRemoveFromScheduleDialog,
    onChangeInternalDeliveryDate,
    onChangeExternalDeliveryDate,
    defaultExpanded,
    isExternal,
    isPrintAvailable,
    isListPrintAvailable,
    onShowMore,
    showMoreVisible,
    loadingMore,
    batchActionSelected,
    checkedOrderIds,
    isAtLimit,
    onHandleCheckedOrders,
}: DeliveryScheduleAccordionProps) => {
    const { t: translate } = useTranslation(['orders']);
    const { printing, feedback, clearFeedback, printList, printDeliveryNotes } = useDeliverySchedulePrint({
        date,
        clinicId,
        departmentId,
    });
    const Icon: FC<IconProps> = ({ style }: IconProps) => {
        switch (style) {
            case 'late':
                return <WarningIcon color="error" />;
            case 'normal':
                return <FormatListBulletedIcon color="info" />;
            case 'completed':
                return <CheckCircleIcon color="success" />;
            default:
                throw new Error(`Invalid style: ${style}`);
        }
    };

    return (
        <Accordion
            defaultExpanded={defaultExpanded || false}
            sx={{ background: style === 'late' ? '#fff5f5' : '#fff' }}
        >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ justifyContent: 'space-between', display: 'flex', width: '100%' }}>
                    <Box sx={{ display: 'flex' }}>
                        <Icon style={style} />
                        <Typography
                            sx={{ marginLeft: 1 }}
                            variant="subtitle2"
                            color={style === 'late' ? 'error' : '#000'}
                        >
                            {title}
                        </Typography>
                    </Box>
                    {isPrintAvailable && (
                        <Box>
                            <DeliverySchedulePrintMenu
                                printing={printing}
                                isListAvailable={!!isListPrintAvailable}
                                onPrintList={printList}
                                onPrintDeliveryNotes={printDeliveryNotes}
                            />
                        </Box>
                    )}
                </Box>
            </AccordionSummary>
            {feedback ? (
                <AlertSnackbar
                    key={feedback.message}
                    type={feedback.type}
                    message={feedback.message}
                    onClose={clearFeedback}
                />
            ) : undefined}
            {printing ? (
                <CircularProgress sx={{ zIndex: 10000, position: 'absolute', top: '50%', left: '50%' }} size={30} />
            ) : (
                <AccordionDetails>
                    <DeliveryScheduleTable
                        orders={orders}
                        onSentByClinicDialog={onSentByClinicDialog}
                        onRemoveFromScheduleDialog={onRemoveFromScheduleDialog}
                        onChangeInternalDeliveryDate={onChangeInternalDeliveryDate}
                        onChangeExternalDeliveryDate={onChangeExternalDeliveryDate}
                        isExternal={isExternal}
                        batchActionSelected={batchActionSelected}
                        checkedOrderIds={checkedOrderIds}
                        isAtLimit={isAtLimit}
                        onHandleCheckedOrders={onHandleCheckedOrders}
                    />
                    {showMoreVisible && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                            <Button variant="contained" size="medium" disabled={!!loadingMore} onClick={onShowMore}>
                                {loadingMore ? <CircularProgress size={20} /> : translate('orders:SHOW_MORE')}
                            </Button>
                        </Box>
                    )}
                </AccordionDetails>
            )}
        </Accordion>
    );
};

export default DeliveryScheduleAccordion;
